/**
 * Neighbour lookups in the browser's tab order, stopping at the first hit.
 *
 * Answering "what is the next focus stop?" by collecting every focusable
 * element in the document and measuring each one forces a synchronous layout
 * across the whole page — on every Tab keypress. These walk outward from the
 * starting element and measure only the candidates they pass, so the cost
 * tracks the distance to the answer rather than the size of the page.
 *
 * Tab order here means document order. That is what the sidebar needs: nothing
 * on these surfaces carries a positive `tabindex`, which is the only thing that
 * would reorder the stops.
 */

/** Everything the browser treats as a tab stop, minus explicit opt-outs. */
export const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Predicate for candidates to pass over, e.g. a subtree that is being left. */
type SkipPredicate = (element: HTMLElement) => boolean;

/**
 * Rendered at all. `getClientRects()` is empty for a `display: none` subtree,
 * which is how collapsed sections and closed portals hide their controls.
 */
function isRendered(element: HTMLElement): boolean {
  return element.getClientRects().length > 0;
}

function walkFocusable(
  from: HTMLElement,
  step: (walker: TreeWalker) => Node | null,
  skip?: SkipPredicate,
): HTMLElement | undefined {
  const doc = from.ownerDocument;
  const root = doc.body ?? doc.documentElement;
  if (!root) return undefined;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) =>
      (node as HTMLElement).matches(FOCUSABLE_SELECTOR)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP,
  });
  walker.currentNode = from;
  for (let node = step(walker); node; node = step(walker)) {
    const element = node as HTMLElement;
    if (skip?.(element)) continue;
    if (isRendered(element)) return element;
  }
  return undefined;
}

/** The first focusable element after `from` in document order. */
export function nextFocusable(from: HTMLElement, skip?: SkipPredicate): HTMLElement | undefined {
  return walkFocusable(from, (walker) => walker.nextNode(), skip);
}

/** The last focusable element before `from` in document order. */
export function previousFocusable(
  from: HTMLElement,
  skip?: SkipPredicate,
): HTMLElement | undefined {
  return walkFocusable(from, (walker) => walker.previousNode(), skip);
}
